import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { ArrowLeft, ArrowRight, Loader2, RefreshCw } from "lucide-react";

const categories = ["stress", "trauma", "physical"];
const categoryLabels = {
  stress: "Current Stress Level",
  trauma: "Emotional State",
  physical: "Physical Wellness"
};

export default function CheckIn() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [questions, setQuestions] = useState({});
  const [currentCategory, setCurrentCategory] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkInHistory, setCheckInHistory] = useState([]);

  useEffect(() => {
    fetchQuestions();
    fetchCheckInHistory();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questionnaire`);
      setQuestions(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load questionnaire");
      setLoading(false);
    }
  };

  const fetchCheckInHistory = async () => {
    try {
      const response = await axios.get(`${API}/check-ins`);
      setCheckInHistory(response.data);
    } catch (error) {
      console.error("Error fetching check-in history:", error);
    }
  };

  const currentCategoryKey = categories[currentCategory];
  const currentQuestions = questions[currentCategoryKey] || [];
  const currentQ = currentQuestions[currentQuestion];
  
  const totalQuestions = categories.reduce((sum, cat) => sum + (questions[cat]?.length || 0), 0);
  const answeredQuestions = Object.keys(responses).length;
  const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  const handleAnswer = (answer) => {
    if (!currentQ) return;
    
    setResponses(prev => ({
      ...prev,
      [currentQ.id]: {
        question_id: currentQ.id,
        question_text: currentQ.text,
        answer: answer,
        category: currentCategoryKey
      }
    }));
  };

  const goNext = () => {
    if (currentQuestion < currentQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else if (currentCategory < categories.length - 1) {
      setCurrentCategory(prev => prev + 1);
      setCurrentQuestion(0);
    }
  };

  const goPrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    } else if (currentCategory > 0) {
      setCurrentCategory(prev => prev - 1);
      const prevCatQuestions = questions[categories[currentCategory - 1]] || [];
      setCurrentQuestion(prevCatQuestions.length - 1);
    }
  };

  const isLastQuestion = currentCategory === categories.length - 1 && 
                         currentQuestion === currentQuestions.length - 1;
  const isFirstQuestion = currentCategory === 0 && currentQuestion === 0;
  const hasCurrentAnswer = currentQ && responses[currentQ.id];

  const handleSubmit = async () => {
    if (answeredQuestions < totalQuestions) {
      toast.error("Please answer all questions before submitting");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/assessment/check-in`, {
        responses: Object.values(responses)
      });
      
      toast.success("Check-in complete! Your meditation plan has been updated.");
      navigate('/dashboard');
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit check-in");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-bg-primary flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cosmic-accent-gold/30 border-t-cosmic-accent-gold rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cosmic-bg-primary relative overflow-hidden" data-testid="checkin-page">
      {/* Subtle background */}
      <div className="absolute inset-0 bg-gradient-to-b from-cosmic-bg-tertiary/30 to-cosmic-bg-primary" />
      
      {/* Progress Circle */}
      <div className="fixed top-6 right-6 z-50">
        <div className="relative w-16 h-16">
          <svg className="w-16 h-16 -rotate-90">
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="4"
            />
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="#30B0C7"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="176"
              strokeDashoffset={176 - (176 * progress) / 100}
              className="transition-all duration-500"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-chakra-throat" />
          </div>
        </div>
      </div>

      {/* Back button */}
      <button
        onClick={() => navigate('/dashboard')}
        className="fixed top-6 left-6 z-50 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-5 h-5 text-cosmic-text-secondary" />
      </button>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center px-6 py-24">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-heading text-3xl text-cosmic-text-primary mb-2">
              Energy Check-In
            </h1>
            <p className="text-cosmic-text-secondary">
              How are you feeling today? Let's update your energy assessment.
            </p>
          </motion.div>

          {/* Category Label */}
          <motion.div
            key={currentCategoryKey}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-chakra-throat/20 border border-chakra-throat/30 text-chakra-throat text-sm">
              {categoryLabels[currentCategoryKey]}
            </span>
          </motion.div>

          {/* Question Card */}
          {currentQ && (
            <motion.div
              key={currentQ.id}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="glass-card p-8 md:p-12"
              data-testid="checkin-question-card"
            >
              <h2 className="font-heading text-2xl md:text-3xl text-cosmic-text-primary mb-8 text-center">
                {currentQ.text}
              </h2>

              <RadioGroup
                value={responses[currentQ.id]?.answer || ""}
                onValueChange={handleAnswer}
                className="space-y-4"
              >
                {currentQ.options.map((option, index) => (
                  <motion.div
                    key={option}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Label
                      htmlFor={`checkin-option-${index}`}
                      className={`flex items-center p-4 rounded-xl cursor-pointer transition-all border ${
                        responses[currentQ.id]?.answer === option
                          ? 'bg-chakra-throat/10 border-chakra-throat/50'
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <RadioGroupItem 
                        value={option} 
                        id={`checkin-option-${index}`}
                        className="border-cosmic-text-muted text-chakra-throat"
                      />
                      <span className="ml-3 text-cosmic-text-primary">{option}</span>
                    </Label>
                  </motion.div>
                ))}
              </RadioGroup>
            </motion.div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button
              onClick={goPrev}
              disabled={isFirstQuestion}
              variant="ghost"
              className="text-cosmic-text-secondary hover:text-cosmic-text-primary disabled:opacity-30"
              data-testid="checkin-prev-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Previous
            </Button>

            {isLastQuestion ? (
              <Button
                onClick={handleSubmit}
                disabled={!hasCurrentAnswer || submitting}
                className="bg-chakra-throat hover:bg-chakra-throat/80 text-white rounded-full px-8 py-4"
                data-testid="checkin-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    Complete Check-In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={goNext}
                disabled={!hasCurrentAnswer}
                className="btn-glass"
                data-testid="checkin-next-btn"
              >
                Next
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>

          {/* Question counter */}
          <div className="text-center mt-8">
            <span className="text-cosmic-text-muted text-sm">
              Question {answeredQuestions + 1} of {totalQuestions}
            </span>
          </div>

          {/* Check-in History */}
          {checkInHistory.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-12"
            >
              <h3 className="text-cosmic-text-secondary text-sm mb-4 text-center">
                Previous Check-ins
              </h3>
              <div className="flex justify-center gap-2 flex-wrap">
                {checkInHistory.slice(0, 5).map((checkin, index) => (
                  <span 
                    key={checkin.checkin_id}
                    className="px-3 py-1 rounded-full bg-white/5 text-xs text-cosmic-text-muted"
                  >
                    {new Date(checkin.created_at).toLocaleDateString()}
                  </span>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
